/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Icon, isEmpty, Modal, ModalBody, ModalFooter, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { FooterButton } from 'components/footer-button'
import { Form, FormRenderProps, resolveName } from 'components/form'
import { IconLabel } from 'components/IconLabel'
import { confirm } from 'components/modals/confirm'
import createDecorator from 'final-form-calculate'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { MutableRefObject, useMemo, useRef, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { metaPath } from 'util/metaPath'

import { consultaSeguimentoCalculator } from '../calculator-consultaSeguimento'
import {
  CONSULTA_SEGUIMENTO_URL_MODAL,
  ConsultaSeguimentoFieldModel,
  ConsultaSeguimentoModalTab,
  ConsultaSeguimentoPerguntaStatusEnum,
  ConsultaSeguimentoSelectionMode,
} from '../model-consultaSeguimento'
import {
  getAplicacaoWithLastQuestionAsNextStatus,
  getAplicacaoWithLastQuestionAsPrevStatus,
  getPerguntaAtiva,
  getPerguntaNextStatus,
  getPerguntasSelecionadasInitialStatus,
} from '../utils-consultaSeguimento'
import { validatorConsultaSeguimento } from '../validator-consultaSeguimento'
import { ConsultaSeguimentoModalFooterCounter } from './ConsultaSeguimentoModalFooterCounter'
import { ConsultaSeguimentoModalStepper } from './ConsultaSeguimentoModalStepper'
import { ConsultaSeguimentoModalTabAdicionar } from './ConsultaSeguimentoModalTabAdicionar'
import { ConsultaSeguimentoModalTabResultado } from './ConsultaSeguimentoModalTabResultado'
import { ConsultaSeguimentoModalTabSelecao } from './ConsultaSeguimentoModalTabSelecao'

interface ConsultaSeguimentoModalProps {
  currentValue: ConsultaSeguimentoFieldModel
  perguntasFalhasNoMChat: MChatPerguntaEnum[]
  aplicarButtonRef: React.RefObject<HTMLButtonElement>
  onSave: (values: ConsultaSeguimentoFieldModel) => void
}

const path = metaPath<ConsultaSeguimentoFieldModel>()
const decorator = createDecorator(...consultaSeguimentoCalculator)

export function ConsultaSeguimentoModal(props: Readonly<ConsultaSeguimentoModalProps>) {
  const { currentValue, perguntasFalhasNoMChat, aplicarButtonRef, onSave } = props

  const theme = useTheme()
  const history = useHistory()
  const match = useRouteMatch()
  const styles = useMemo(() => createStyles(theme), [theme])

  const hasValues = !isEmpty(currentValue)

  const backToSelecaoButtonRef = useRef<HTMLButtonElement>()
  const backToAplicacaoButtonRef = useRef<HTMLButtonElement>()
  const backQuestionButtonRef = useRef<HTMLButtonElement>()
  const modalContentRef = useRef<HTMLDivElement>()
  const numberOfPerguntasFalhasNoMChat = perguntasFalhasNoMChat?.length
  const hasPerguntasFalhasNoMChat = numberOfPerguntasFalhasNoMChat > 0
  const isMChatRiscoBaixo = hasPerguntasFalhasNoMChat && numberOfPerguntasFalhasNoMChat < 3

  const [currentTab, setCurrentTab] = useState<ConsultaSeguimentoModalTab>(
    hasValues ? ConsultaSeguimentoModalTab.RESULTADO : ConsultaSeguimentoModalTab.SELECAO
  )
  const [startFailed, setStartFailed] = useState(false)

  const initialValues: MutableRefObject<ConsultaSeguimentoFieldModel> = useRef({
    perguntasSelecionadas: perguntasFalhasNoMChat,
    aplicacao: null,
    selectionMode:
      hasPerguntasFalhasNoMChat && !isMChatRiscoBaixo
        ? ConsultaSeguimentoSelectionMode.AUTOMATICO
        : ConsultaSeguimentoSelectionMode.MANUAL,
  })

  const handleStart = ({ values, form }: FormRenderProps<ConsultaSeguimentoFieldModel>) => {
    const isSelectionModeAutomatico = values.selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO
    const isSelectionModeManual = values.selectionMode === ConsultaSeguimentoSelectionMode.MANUAL
    const sortedPerguntasSelecionadas = [...values.perguntasSelecionadas].sort((a, b) => a.localeCompare(b))

    const perguntasSelecionadasBySelectionMode = isSelectionModeAutomatico
      ? perguntasFalhasNoMChat
      : sortedPerguntasSelecionadas

    const isInvalid = isSelectionModeManual && !form.getFieldState(resolveName(path.perguntasSelecionadas)).valid

    if (isInvalid) {
      setStartFailed(true)
      modalContentRef.current.scrollTo({ behavior: 'smooth', top: 0 })
      return null
    }

    form.change('', {
      selectionMode: values.selectionMode,
      perguntasSelecionadas: perguntasSelecionadasBySelectionMode,
      aplicacao: getPerguntasSelecionadasInitialStatus(perguntasSelecionadasBySelectionMode),
    })

    setCurrentTab(ConsultaSeguimentoModalTab.APLICACAO)
    setTimeout(() => backToSelecaoButtonRef.current?.focus())
  }

  const handleNext = ({ values, form }: FormRenderProps<ConsultaSeguimentoFieldModel>) => {
    const { perguntaAtiva, indexOfPerguntaAtiva, isLastPergunta } = getPerguntaAtiva(values.aplicacao)

    const isInvalid = !form.getFieldState(resolveName(path.aplicacao[perguntaAtiva])).valid
    if (isInvalid) return null

    if (!isLastPergunta) {
      const valuesOfPerguntaAtiva = values.aplicacao[perguntaAtiva]

      form.change(resolveName(path.aplicacao[perguntaAtiva]), {
        ...valuesOfPerguntaAtiva,
        status: getPerguntaNextStatus(valuesOfPerguntaAtiva.status),
      })

      const nextPergunta = values.perguntasSelecionadas[indexOfPerguntaAtiva + 1]

      form.change(resolveName(path.aplicacao[nextPergunta]), {
        ...values.aplicacao[nextPergunta],
        status: ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
      })

      setTimeout(() => backQuestionButtonRef.current.focus())
    }

    if (isLastPergunta) {
      onSave({ ...values, aplicacao: getAplicacaoWithLastQuestionAsNextStatus(values.aplicacao) })
      setCurrentTab(ConsultaSeguimentoModalTab.RESULTADO)
      setTimeout(() => backToAplicacaoButtonRef.current.focus())
    }
  }

  const handleCloseModal = () => {
    const goBack = () => {
      history.push(match.url.split(CONSULTA_SEGUIMENTO_URL_MODAL)[0])
      setTimeout(() => aplicarButtonRef?.current?.focus())
    }

    if (currentTab === ConsultaSeguimentoModalTab.APLICACAO) {
      closeModalConfirmDialog(goBack)
    } else {
      goBack()
    }
  }

  const handleGoBackToAplicacao = (form: FormRenderProps<ConsultaSeguimentoFieldModel>['form']) => {
    form.reset({ ...currentValue, aplicacao: getAplicacaoWithLastQuestionAsPrevStatus(currentValue.aplicacao) })
    setCurrentTab(ConsultaSeguimentoModalTab.APLICACAO)
  }

  const handleGoBackToSelecao = () => {
    setCurrentTab(ConsultaSeguimentoModalTab.SELECAO)
  }

  const renderModal = (renderProps: FormRenderProps<ConsultaSeguimentoFieldModel>) => {
    const { perguntaAtiva, isLastPergunta } = getPerguntaAtiva(renderProps.values.aplicacao)
    const errors = renderProps.errors ?? {}
    const isInvalid = perguntaAtiva in errors.aplicacao

    return (
      <Modal open closeOnBackdropClick={false} onClose={handleCloseModal} style={styles.modal} hasCloseIcon={false}>
        <HFlow hSpacing={0} style={styles.hFlow}>
          <div css={styles.sidebar}>
            <ConsultaSeguimentoModalStepper perguntas={renderProps.values.aplicacao} status={currentTab} />
          </div>
          <ModalBody style={styles.modalBody}>
            <div css={styles.modalContent} ref={modalContentRef}>
              {currentTab === ConsultaSeguimentoModalTab.SELECAO && (
                <ConsultaSeguimentoModalTabSelecao
                  path={path}
                  perguntasFalhasNoMChat={perguntasFalhasNoMChat}
                  startFailed={startFailed}
                  onClose={handleCloseModal}
                  clearStartFailed={() => setStartFailed(false)}
                />
              )}
              {currentTab === ConsultaSeguimentoModalTab.APLICACAO && (
                <ConsultaSeguimentoModalTabAdicionar
                  path={path.aplicacao}
                  values={renderProps.values}
                  backToSelecaoButtonRef={backToSelecaoButtonRef}
                  backQuestionButtonRef={backQuestionButtonRef}
                  onGoBackToSelecao={handleGoBackToSelecao}
                  onClose={handleCloseModal}
                />
              )}
              {currentTab === ConsultaSeguimentoModalTab.RESULTADO && (
                <ConsultaSeguimentoModalTabResultado
                  value={currentValue}
                  backToAplicacaoButtonRef={backToAplicacaoButtonRef}
                  onGoBackToAplicacao={() => handleGoBackToAplicacao(renderProps.form)}
                  onClose={handleCloseModal}
                />
              )}
            </div>
          </ModalBody>
        </HFlow>

        <ModalFooter style={styles.modalFooter}>
          <HFlow justifyContent='flex-end' alignItems='center'>
            {currentTab === ConsultaSeguimentoModalTab.RESULTADO && (
              <IconLabel
                icon='checkCircleOutline'
                text='Resultado salvo no atendimento'
                color={theme.pallete.status.success.c40}
              />
            )}

            {currentTab === ConsultaSeguimentoModalTab.SELECAO && (
              <ConsultaSeguimentoModalFooterCounter
                name={path.perguntasSelecionadas}
                selectionMode={renderProps.values.selectionMode}
                isMChatRiscoBaixo={isMChatRiscoBaixo}
                numberOfPerguntasFalhasNoMChat={numberOfPerguntasFalhasNoMChat}
                startFailed={startFailed}
              />
            )}

            <FooterButton onClick={handleCloseModal}>
              {currentTab === ConsultaSeguimentoModalTab.RESULTADO ? 'Fechar' : 'Cancelar'}
            </FooterButton>

            {currentTab === ConsultaSeguimentoModalTab.SELECAO && (
              <FooterButton kind='primary' onClick={() => handleStart(renderProps)}>
                Iniciar
              </FooterButton>
            )}

            {currentTab === ConsultaSeguimentoModalTab.APLICACAO && (
              <FooterButton kind='primary' onClick={() => handleNext(renderProps)} disabled={isInvalid}>
                {isLastPergunta ? 'Calcular resultado' : 'Continuar'}
              </FooterButton>
            )}
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<ConsultaSeguimentoFieldModel>
      initialValues={initialValues.current}
      render={renderModal}
      validate={validatorConsultaSeguimento}
      decorators={[decorator]}
    />
  )
}

const closeModalConfirmDialog = (handleConfirm: () => void) => {
  confirm({
    confirmLabel: 'Sair da tela',
    cancelLabel: 'Continuar aqui',
    type: 'primary',
    size: 'small',
    onConfirm: handleConfirm,
    render: () => (
      <HFlow>
        <Icon icon='exclamationTriangleOutline' fill='alert' size={3} />
        <VFlow vSpacing={0.125}>
          <Heading level={1}>Deseja sair sem finalizar a Entrevista de Seguimento?</Heading>
          <Text>As alterações realizadas serão perdidas, inclusive resultados já calculados.</Text>
        </VFlow>
      </HFlow>
    ),
  })()
}

const createStyles = (theme: Theme) => ({
  modal: css`
    display: grid;
    grid-template-rows: 1fr auto;
    width: 62rem;
    max-width: 62rem;
    overflow: hidden;
    height: 80vh;
  `,
  hFlow: css`
    max-height: inherit;
    grid-auto-columns: 17rem auto;
  `,
  sidebar: css`
    min-width: 17rem;
    max-height: calc(80vh - 80px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 3.75rem 2rem 1rem 1.5rem;
    background: ${theme.pallete.gray.c90};
    border-right: 1px solid ${theme.pallete.divider};
    overflow-y: auto;
  `,
  modalBody: css`
    padding: 0;
    overflow: auto;
    max-height: inherit;
  `,
  modalContent: css`
    overflow: hidden auto;
    max-height: calc(80vh - 5rem);
    padding: 1rem 1.5rem 2.5rem 2.5rem;
  `,
  modalFooter: css`
    border-top: 1px solid ${theme.pallete.divider};
  `,
})
